import { supabase } from "@/lib/supabase";

/**
 * Activity-log service. Reads + writes the `activity_log` table from
 * migration 0011. Writes go through the `record_activity` RPC so team_id
 * is stamped from the caller's JWT (the table has no INSERT policy).
 *
 * Design choice: write failures NEVER propagate to the caller. The activity
 * log is observability — if it's broken, the user's import / route create /
 * sign-in should still succeed. Errors are logged to the console only.
 */

export type ActivityKind =
  | "sign_in"
  | "sign_out"
  | "lead_import"
  | "lead_export"
  | "lead_delete"
  | "lead_assign"
  | "route_create"
  | "route_cancel"
  | "route_delete"
  | "route_reassign"
  | "route_stop_add"
  | "route_stop_remove"
  | "member_delete"
  | "member_role_change"
  | "member_active_change"
  | "zone_create"
  | "zone_delete"
  | "zone_assign"
  | "depot_create"
  | "depot_delete"
  | "appointment_set"
  | "appointment_status_change"
  | "password_reset_requested"
  | "password_changed";

export interface ActivityRow {
  id: string;
  team_id: string;
  user_id: string | null;
  kind: ActivityKind;
  target_type: string | null;
  target_id: string | null;
  summary: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  /** Joined display name of the actor — null when the user row was deleted
   *  (the FK is `on delete set null`, so the row stays but actor goes blank). */
  actor_name: string | null;
  actor_email: string | null;
}

interface RecordActivityInput {
  kind: ActivityKind;
  target_type?: string;
  target_id?: string;
  summary?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Fire-and-forget activity write. Never throws — the worst case is a missed
 * log line, which is preferable to a user-facing error after a successful
 * import / route save.
 */
export async function recordActivity(input: RecordActivityInput): Promise<void> {
  try {
    const { error } = await supabase.rpc("record_activity", {
      p_kind: input.kind,
      p_target_type: input.target_type ?? null,
      p_target_id: input.target_id ?? null,
      p_summary: input.summary ?? null,
      p_metadata: input.metadata ?? {},
    });
    if (error) {
      console.warn("[activity_log] skipped:", error.message);
    }
  } catch (e) {
    console.warn("[activity_log] threw:", e);
  }
}

export interface ListActivityParams {
  /** Filter to one or more event kinds. Empty/undefined = all kinds. */
  kinds?: ActivityKind[];
  /** Max rows to return. Default 200; UI paginates above this. */
  limit?: number;
}

export async function listActivity(params: ListActivityParams = {}): Promise<ActivityRow[]> {
  const limit = Math.min(Math.max(params.limit ?? 200, 1), 1000);

  let query = supabase
    .from("activity_log")
    .select(
      "id, team_id, user_id, kind, target_type, target_id, summary, metadata, created_at, actor:app_users!activity_log_user_id_fkey(full_name, email)",
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (params.kinds && params.kinds.length > 0) {
    query = query.in("kind", params.kinds);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((r) => {
    const actor = (r as unknown as { actor: { full_name: string; email: string } | null }).actor;
    return {
      id: r.id,
      team_id: r.team_id,
      user_id: r.user_id,
      kind: r.kind as ActivityKind,
      target_type: r.target_type,
      target_id: r.target_id,
      summary: r.summary,
      metadata: (r.metadata ?? {}) as Record<string, unknown>,
      created_at: r.created_at,
      actor_name: actor?.full_name ?? null,
      actor_email: actor?.email ?? null,
    };
  });
}
